import { fork, call, takeLatest, put, all } from 'redux-saga/effects'
import { push } from 'connected-react-router'
import moment from 'moment'
import { unauthorizedAction } from '../actions/authActions'
import {
  createReportAction,
  getAllReportsAction,
  getPublicReportAction,
  getAllReportConfigsAction,
  createReportConfigAction,
  updateReportConfigAction,
  deleteReportConfigAction,
} from '../actions/reportsActions'
import {
  showSuccessMessageAction,
  showErrorMessageAction,
} from '../actions/uiActions'
import api from '../services/api'
import { MESSAGES } from '../constants'
import { pickErrorMessage } from '../utils/helpers'
import { createQueryString } from '../utils/queryParams'

// The backend fetches the previous period as the same length of time
// directly before the report range, so move it forward by that length to
// line it up with the report range.
const shiftPreviousPeriod = ({ pages, fromTimestamp, toTimestamp }) => {
  const periodSeconds = toTimestamp - fromTimestamp

  return pages.map(page => ({
    ...page,
    items: page.items.map(item =>
      item.type === 'LAST_AVERAGE'
        ? {
            ...item,
            data: item.data.map(z => ({
              ...z,
              originalTime: z.time,
              time: moment(z.time)
                .add(periodSeconds, 'seconds')
                .toISOString(),
            })),
          }
        : item
    ),
  }))
}

// handlers

function* handleCreateReportSaga({ payload }) {
  try {
    const result = yield call(api.createReport, payload)

    const pages = shiftPreviousPeriod(result)

    yield put(createReportAction.success({ ...result, pages }))
    yield put(showSuccessMessageAction(MESSAGES.SUCCESSFULLY_GENERATED_REPORT))

    // TODO
    // yield put(push('/reports'))
  } catch (error) {
    yield put(showErrorMessageAction(pickErrorMessage(error)))

    if (error.status === 401) {
      yield put(unauthorizedAction())
    }
  }
}

function* handleGetAllReportsSaga({ payload }) {
  try {
    const result = yield call(api.getAllReports, payload)

    yield put(getAllReportsAction.success(result))
  } catch (error) {
    yield put(getAllReportsAction.failure(error))
    yield put(showErrorMessageAction(pickErrorMessage(error)))

    if (error.status === 401) {
      yield put(unauthorizedAction())
    }
  }
}

function* handleGetPublicReportSaga({ payload }) {
  try {
    const result = yield call(api.getPublicReportByReportSlug, payload)

    const pages = shiftPreviousPeriod(result)

    yield put(getPublicReportAction.success({ ...result, pages }))
  } catch (error) {
    yield put(getPublicReportAction.failure(error))
    yield put(showErrorMessageAction(pickErrorMessage(error)))

    if (error.status === 401) {
      yield put(unauthorizedAction())
    }
  }
}

function* handleGetAllReportConfigsSaga({ payload }) {
  try {
    const result = yield call(api.getAllReportConfigs, payload)

    yield put(getAllReportConfigsAction.success(result))
  } catch (error) {
    yield put(getAllReportConfigsAction.failure(error))
    yield put(showErrorMessageAction(pickErrorMessage(error)))

    if (error.status === 401) {
      yield put(unauthorizedAction())
    }
  }
}

function* handleCreateReportConfigSaga({ payload }) {
  try {
    const result = yield call(api.createReportConfig, payload)
    yield put(createReportConfigAction.success(result))
    yield put(showSuccessMessageAction(MESSAGES.SUCCESSFULLY_CREATED))

    yield fork(handleGetAllReportConfigsSaga, {
      payload: createQueryString({ perPage: 'all' }),
    })

    yield put(push('/reports/configs'))
  } catch (error) {
    yield put(showErrorMessageAction(pickErrorMessage(error)))

    if (error.status === 401) {
      yield put(unauthorizedAction())
    }
  }
}

function* handleUpdateReportConfigSaga({ payload }) {
  try {
    const result = yield call(api.updateReportConfig, payload)
    yield put(updateReportConfigAction.success(result))
    yield put(showSuccessMessageAction(MESSAGES.SUCCESSFULLY_UPDATED))

    yield fork(handleGetAllReportConfigsSaga, {
      payload: createQueryString({ perPage: 'all' }),
    })

    yield put(push('/reports/configs'))
  } catch (error) {
    yield put(showErrorMessageAction(pickErrorMessage(error)))

    if (error.status === 401) {
      yield put(unauthorizedAction())
    }
  }
}

function* handleDeleteReportConfigSaga({ payload }) {
  try {
    const confirmDeletion = yield call(global.confirm, MESSAGES.CONFIRM)

    if (confirmDeletion) {
      yield call(api.deleteReportConfig, payload)
      yield put(deleteReportConfigAction.success())
      yield put(showSuccessMessageAction(MESSAGES.SUCCESSFULLY_DELETED))

      yield fork(handleGetAllReportConfigsSaga, {
        payload: createQueryString({ perPage: 'all' }),
      })
    }
  } catch (error) {
    yield put(showErrorMessageAction(pickErrorMessage(error)))

    if (error.status === 401) {
      yield put(unauthorizedAction())
    }
  }
}

// watchers

function* watchCreateReportSaga() {
  yield takeLatest(createReportAction.REQUEST, handleCreateReportSaga)
}

function* watchGetAllReportsSaga() {
  yield takeLatest(getAllReportsAction.REQUEST, handleGetAllReportsSaga)
}

function* watchGetPublicReportSaga() {
  yield takeLatest(getPublicReportAction.REQUEST, handleGetPublicReportSaga)
}

function* watchGetAllReportConfigsSaga() {
  yield takeLatest(
    getAllReportConfigsAction.REQUEST,
    handleGetAllReportConfigsSaga
  )
}

function* watchCreateReportConfigSaga() {
  yield takeLatest(
    createReportConfigAction.REQUEST,
    handleCreateReportConfigSaga
  )
}

function* watchUpdateReportConfigSaga() {
  yield takeLatest(
    updateReportConfigAction.REQUEST,
    handleUpdateReportConfigSaga
  )
}

function* watchDeleteReportConfigSaga() {
  yield takeLatest(
    deleteReportConfigAction.REQUEST,
    handleDeleteReportConfigSaga
  )
}

function* reportsSaga() {
  yield all([
    fork(watchCreateReportSaga),
    fork(watchGetAllReportsSaga),
    fork(watchGetPublicReportSaga),
    fork(watchGetAllReportConfigsSaga),
    fork(watchCreateReportConfigSaga),
    fork(watchUpdateReportConfigSaga),
    fork(watchDeleteReportConfigSaga),
  ])
}

export { reportsSaga as default }
